// ShareASale / Awin affiliate conversion tracking.
//
// The tags themselves live in GTM container GTM-NSPR45 (Awin Advertiser
// MasterTag + AW Last Click Identifier + Awin Conversion Tag, all from the GTM
// Community Template Gallery). This module's only job is to hand GTM the
// transaction data through the dataLayer.
//
// Spec: ai_docs/specs/shareasale-awin-conversion-tracking.md

const DEDUP_STORAGE_KEY = "awin_reported_piid";

export interface AwinPurchase {
  /** `credit_card_transaction.id` from the thank-you API — NOT the payment intent. */
  transactionId?: string | number;
  /** EUR base amount from the thank-you API — NOT the charged amount. */
  amount?: string | number;
  /** Always "EUR" today; passed through so a backend change propagates. */
  currency?: string;
  couponCode?: string;
}

const pushToDataLayer = (payload: Record<string, unknown>): void => {
  if (typeof window === "undefined") {
    return;
  }
  // Push through `window.dataLayer` rather than any direct tag call: the array
  // survives being written before GTM has loaded, and GTM replays it on init.
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(payload);
};

/**
 * Tell GTM the cookie bar has been accepted, so the Awin tags have something to
 * gate on. Safe to call more than once — GTM triggers on the event, and a repeat
 * accept from an already-consented visitor is not a new conversion.
 */
export const pushConsentAccepted = (): void => {
  pushToDataLayer({ event: "cookie_consent_accepted" });
};

/**
 * Push the Awin conversion into the dataLayer exactly once per payment intent.
 *
 * Dedup matters here because the thank-you pages re-poll the API up to 10 times
 * while they wait for the eSIM to be provisioned — without the guard, every poll
 * would report the sale again. It mirrors the legacy www3 behaviour, which stored
 * the payment-intent id in localStorage.
 *
 * The storage key is deliberately NOT the legacy "piid": a customer who still has
 * the old www3 value in localStorage would otherwise be silently skipped on their
 * first purchase through this flow.
 *
 * No-ops when the transaction isn't resolved yet, so it is safe to call on every
 * poll rather than only on the final one — the sale is reported as soon as it is
 * known, independently of whether provisioning has finished.
 */
export const reportAwinPurchase = (
  paymentIntentId: string,
  purchase: AwinPurchase,
): void => {
  if (typeof window === "undefined" || paymentIntentId === "") {
    return;
  }
  const { transactionId, amount, currency, couponCode } = purchase;
  if (
    transactionId === undefined ||
    transactionId === null ||
    transactionId === "" ||
    amount === undefined ||
    amount === null ||
    amount === "" ||
    currency === undefined ||
    currency === ""
  ) {
    return;
  }

  try {
    if (window.localStorage.getItem(DEDUP_STORAGE_KEY) === paymentIntentId) {
      return;
    }
    window.localStorage.setItem(DEDUP_STORAGE_KEY, paymentIntentId);
  } catch {
    // Private mode / blocked storage. Fall through and report anyway: a rare
    // double-count is preferable to losing every conversion for these visitors.
  }

  pushToDataLayer({
    event: "awin_purchase",
    awin: {
      orderReference: String(transactionId),
      amount: String(amount),
      currency,
      voucherCode: couponCode ?? "",
    },
  });
};
